import * as THREE from 'three/webgpu';

const UP = new THREE.Vector3( 0, 1, 0 );

export const CAMERA_SHOTS = Object.freeze( {
	exterior: {
		label: 'Exterior',
		position: [ 80, 108, 680 ],
		target: [ - 45, 182, - 40 ]
	},
	interior: {
		label: 'Inside',
		position: [ - 125, 178, 80 ],
		target: [ 220, 188, - 150 ]
	},
	above: {
		label: 'Above',
		position: [ 75, 375, 315 ],
		target: [ - 40, 175, - 60 ]
	},
	grazing: {
		label: 'Sun edge',
		position: [ 315, 122, 390 ],
		target: [ - 90, 180, - 120 ]
	}
} );

export class FlightControls {

	constructor( camera, element ) {

		this.camera = camera;
		this.element = element;
		this.enabled = true;
		this.speed = 72;
		this.lookSpeed = 0.0024;
		this.dragging = false;
		this.pointerId = null;
		this.keys = new Set();
		this._yaw = 0;
		this._pitch = 0;
		this._lastX = 0;
		this._lastY = 0;
		this._forward = new THREE.Vector3();
		this._right = new THREE.Vector3();
		this._movement = new THREE.Vector3();

		this.#bind();
		this.setShot( 'exterior' );

	}

	#bind() {

		this._onPointerDown = ( event ) => {

			if ( event.button !== 0 || event.target.closest?.( '.cloud-panel, .shot-dock' ) ) return;
			this.dragging = true;
			this.pointerId = event.pointerId;
			this._lastX = event.clientX;
			this._lastY = event.clientY;
			this.element.setPointerCapture?.( event.pointerId );
			this.element.classList.add( 'is-dragging' );

		};

		this._onPointerMove = ( event ) => {

			if ( ! this.dragging || event.pointerId !== this.pointerId ) return;
			const dx = event.clientX - this._lastX;
			const dy = event.clientY - this._lastY;
			this._lastX = event.clientX;
			this._lastY = event.clientY;
			this._yaw -= dx * this.lookSpeed;
			this._pitch = Math.max( - 1.48, Math.min( 1.48, this._pitch - dy * this.lookSpeed ) );
			this.#applyRotation();

		};

		this._onPointerUp = ( event ) => {

			if ( event.pointerId !== this.pointerId ) return;
			this.dragging = false;
			this.pointerId = null;
			this.element.classList.remove( 'is-dragging' );

		};

		this._onKeyDown = ( event ) => {

			if ( event.target instanceof HTMLInputElement || event.target instanceof HTMLSelectElement ) return;
			this.keys.add( event.code );

		};

		this._onKeyUp = ( event ) => this.keys.delete( event.code );
		this._onWheel = ( event ) => {

			if ( event.target.closest?.( '.cloud-panel' ) ) return;
			this.speed = Math.max( 12, Math.min( 260, this.speed * Math.exp( - event.deltaY * 0.001 ) ) );

		};

		this.element.addEventListener( 'pointerdown', this._onPointerDown );
		this.element.addEventListener( 'pointermove', this._onPointerMove );
		this.element.addEventListener( 'pointerup', this._onPointerUp );
		this.element.addEventListener( 'pointercancel', this._onPointerUp );
		this.element.addEventListener( 'wheel', this._onWheel, { passive: true } );
		window.addEventListener( 'keydown', this._onKeyDown );
		window.addEventListener( 'keyup', this._onKeyUp );

	}

	#applyRotation() {

		this.camera.rotation.set( this._pitch, this._yaw, 0, 'YXZ' );

	}

	setShot( name ) {

		const shot = CAMERA_SHOTS[ name ] ?? CAMERA_SHOTS.exterior;
		this.camera.position.fromArray( shot.position );
		this.camera.lookAt( new THREE.Vector3().fromArray( shot.target ) );
		this.camera.rotation.reorder( 'YXZ' );
		this._pitch = this.camera.rotation.x;
		this._yaw = this.camera.rotation.y;
		return shot;

	}

	update( deltaSeconds ) {

		if ( ! this.enabled ) return;

		this.camera.getWorldDirection( this._forward );
		this._right.crossVectors( this._forward, UP ).normalize();
		this._movement.set( 0, 0, 0 );

		if ( this.keys.has( 'KeyW' ) ) this._movement.add( this._forward );
		if ( this.keys.has( 'KeyS' ) ) this._movement.sub( this._forward );
		if ( this.keys.has( 'KeyD' ) ) this._movement.add( this._right );
		if ( this.keys.has( 'KeyA' ) ) this._movement.sub( this._right );
		if ( this.keys.has( 'KeyE' ) || this.keys.has( 'Space' ) ) this._movement.add( UP );
		if ( this.keys.has( 'KeyQ' ) || this.keys.has( 'ControlLeft' ) ) this._movement.sub( UP );

		if ( this._movement.lengthSq() > 0 ) {

			const boost = this.keys.has( 'ShiftLeft' ) || this.keys.has( 'ShiftRight' ) ? 3 : 1;
			this.camera.position.addScaledVector( this._movement.normalize(), this.speed * boost * deltaSeconds );

		}

	}

	dispose() {

		this.element.removeEventListener( 'pointerdown', this._onPointerDown );
		this.element.removeEventListener( 'pointermove', this._onPointerMove );
		this.element.removeEventListener( 'pointerup', this._onPointerUp );
		this.element.removeEventListener( 'pointercancel', this._onPointerUp );
		this.element.removeEventListener( 'wheel', this._onWheel );
		window.removeEventListener( 'keydown', this._onKeyDown );
		window.removeEventListener( 'keyup', this._onKeyUp );

	}

}
